import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { Combobox } from "@dashboard/components/Combobox";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Pill } from "@dashboard/components/Pill";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import WsmProductsField from "@dashboard/wsmCompose/components/WsmProductsField/WsmProductsField";
import { getWsmSeriesErrorMessage, getWsmSeriesFieldError } from "@dashboard/wsmSeries/errors";
import { statusMessages } from "@dashboard/wsmSeries/messages";
import { WsmSeriesFormData } from "@dashboard/wsmSeries/types";
import { wsmSeriesListPath } from "@dashboard/wsmSeries/urls";
import {
  getAxisChoices,
  getAxisLabels,
  getPartitioningAxisChoices,
  toggleSeriesAxis,
} from "@dashboard/wsmSeries/utils";
import { Box, Checkbox, Input, Select, Skeleton, Text, Textarea } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { messages } from "./messages";

export interface WsmSeriesDetailsPageProps {
  title: string;
  memberCount?: number | null;
  /** The store's own product attributes: what the tick-boxes are built from. */
  attributes: Array<{ slug?: string | null; name?: string | null }>;
  attributesLoading?: boolean;
  /** The store's brands, by name: what the brand picker offers. */
  brands: Array<{ id: string; name: string }>;
  initialData: WsmSeriesFormData;
  errors: WsmErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmSeriesFormData) => void;
  onDelete?: () => void;
}

export const WsmSeriesDetailsPage = ({
  title,
  memberCount,
  attributes,
  attributesLoading,
  brands,
  initialData,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
  onDelete,
}: WsmSeriesDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmSeriesFormData>(initialData);
  const change = (patch: Partial<WsmSeriesFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const axisChoices = getAxisChoices(attributes, data.axes);
  const partitioningChoices = getPartitioningAxisChoices(data.axes, axisChoices);
  // A series carries its brand as a string until WSM6-3051 points it at the
  // Brand row, so the picker offers the store's brands and takes a name too.
  const brandOptions = brands.map(brand => ({ label: brand.name, value: brand.name }));
  const fieldError = (field: string) => getWsmSeriesFieldError(errors, field);
  const helper = (field: string, fallback?: string) =>
    getWsmSeriesErrorMessage(fieldError(field), intl) ?? fallback;

  return (
    <DetailPageLayout>
      <TopNav href={wsmSeriesListPath} title={title} />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-series-general">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(messages.generalTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={4}>
              <Input
                label={intl.formatMessage(messages.name)}
                data-test-id="wsm-series-name"
                disabled={disabled}
                value={data.name}
                onChange={event => change({ name: event.target.value })}
                error={!!fieldError("name")}
                helperText={helper("name")}
              />
              <Input
                label={intl.formatMessage(messages.slug)}
                data-test-id="wsm-series-slug"
                disabled={disabled}
                value={data.slug}
                onChange={event => change({ slug: event.target.value })}
                error={!!fieldError("slug")}
                helperText={helper("slug", intl.formatMessage(messages.slugHelp))}
              />
            </Box>
            <FormSpacer />
            <Combobox
              name="brand"
              label={intl.formatMessage(messages.brand)}
              data-test-id="wsm-series-brand"
              disabled={disabled}
              options={brandOptions}
              // The whole list is already here; nothing to fetch on typing.
              fetchOptions={() => undefined}
              value={data.brand ? { label: data.brand, value: data.brand } : null}
              onChange={event => change({ brand: event.target.value ?? "" })}
              error={!!fieldError("brand")}
              helperText={helper("brand", intl.formatMessage(messages.brandHelp))}
              allowCustomValues
            />
            <FormSpacer />
            <Input
              label={intl.formatMessage(messages.tagline)}
              data-test-id="wsm-series-tagline"
              disabled={disabled}
              value={data.tagline}
              onChange={event => change({ tagline: event.target.value })}
              error={!!fieldError("tagline")}
              helperText={helper("tagline")}
            />
            <FormSpacer />
            <Textarea
              rows={4}
              label={intl.formatMessage(messages.description)}
              data-test-id="wsm-series-description"
              disabled={disabled}
              value={data.description}
              onChange={event => change({ description: event.target.value })}
              error={!!fieldError("description")}
              helperText={helper("description")}
            />
            <FormSpacer />
            <Textarea
              rows={4}
              label={intl.formatMessage(messages.extendedDescription)}
              data-test-id="wsm-series-extended-description"
              disabled={disabled}
              value={data.extendedDescription}
              onChange={event => change({ extendedDescription: event.target.value })}
              error={!!fieldError("extendedDescription")}
              helperText={helper(
                "extendedDescription",
                intl.formatMessage(messages.extendedDescriptionHelp),
              )}
            />
            <FormSpacer />
            <Input
              label={intl.formatMessage(messages.image)}
              data-test-id="wsm-series-image"
              disabled={disabled}
              value={data.image}
              onChange={event => change({ image: event.target.value })}
              error={!!fieldError("image")}
              helperText={helper("image", intl.formatMessage(messages.imageHelp))}
            />
            <FormSpacer />
            <Input
              label={intl.formatMessage(messages.missMessage)}
              data-test-id="wsm-series-miss-message"
              disabled={disabled}
              value={data.missMessage}
              onChange={event => change({ missMessage: event.target.value })}
              error={!!fieldError("missMessage")}
              helperText={helper("missMessage", intl.formatMessage(messages.missMessageHelp))}
            />
          </DashboardCard.Content>
        </DashboardCard>

        <CardSpacer />
        <DashboardCard data-test-id="wsm-series-products">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(messages.productsTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <WsmProductsField
              data-test-id="wsm-series-products-field"
              label={intl.formatMessage(messages.products)}
              helperText={intl.formatMessage(messages.productsHelp)}
              value={data.products}
              disabled={disabled}
              error={getWsmSeriesErrorMessage(fieldError("products"), intl)}
              onChange={products => change({ products })}
            />
          </DashboardCard.Content>
        </DashboardCard>

        <CardSpacer />
        <DashboardCard data-test-id="wsm-series-axes">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(messages.axesTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Text size={2} color="default2">
              {intl.formatMessage(messages.axesSubtitle)}
            </Text>
            <FormSpacer />
            {attributesLoading && <Skeleton />}
            {!attributesLoading && axisChoices.length === 0 && (
              <Text size={3} color="default2" data-test-id="wsm-series-axes-empty">
                {intl.formatMessage(messages.axesEmpty)}
              </Text>
            )}
            <Box display="grid" gap={2}>
              {axisChoices.map(choice => (
                <Checkbox
                  key={choice.slug}
                  name={`axis-${choice.slug}`}
                  data-test-id={`wsm-series-axis-${choice.slug}`}
                  disabled={disabled}
                  checked={choice.ticked}
                  onCheckedChange={checked =>
                    setData(current => toggleSeriesAxis(current, choice.slug, !!checked))
                  }
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* The attribute's name leads, title-cased at the boundary;
                        the slug is secondary and never stands on its own. */}
                    <Text size={3}>{choice.name}</Text>
                    <Text size={2} color="default2">
                      {choice.slug}
                    </Text>
                    {!choice.known && (
                      <Text size={2} color="critical1">
                        {intl.formatMessage(messages.axisUnknown)}
                      </Text>
                    )}
                  </Box>
                </Checkbox>
              ))}
            </Box>
            {data.axes.length > 0 && (
              <>
                <FormSpacer />
                <Text size={2} color="default2" data-test-id="wsm-series-axes-order">
                  {intl.formatMessage(messages.axesOrder, {
                    order: getAxisLabels(data.axes, attributes).join(", "),
                  })}
                </Text>
              </>
            )}
            {fieldError("axes") && (
              <>
                <FormSpacer />
                <Text size={2} color="critical1" data-test-id="wsm-series-axes-error">
                  {getWsmSeriesErrorMessage(fieldError("axes"), intl)}
                </Text>
              </>
            )}
            <FormSpacer />
            <Select
              label={intl.formatMessage(messages.partitioningAxis)}
              data-test-id="wsm-series-partitioning-axis"
              disabled={disabled || partitioningChoices.length === 0}
              options={partitioningChoices}
              value={data.partitioningAxis}
              onChange={value => change({ partitioningAxis: value as string })}
              error={!!fieldError("partitioningAxis")}
              helperText={helper(
                "partitioningAxis",
                intl.formatMessage(messages.partitioningAxisHelp),
              )}
            />
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        <DashboardCard data-test-id="wsm-series-availability">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(messages.availabilityTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Pill
              data-test-id="wsm-series-status"
              color={data.published ? "success" : "error"}
              label={intl.formatMessage(
                data.published ? statusMessages.live : statusMessages.draft,
              )}
            />
            <FormSpacer />
            <Checkbox
              data-test-id="wsm-series-published"
              disabled={disabled}
              name="published"
              checked={data.published}
              onCheckedChange={checked => change({ published: !!checked })}
            >
              <Text size={3}>{intl.formatMessage(messages.published)}</Text>
            </Checkbox>
            {fieldError("published") && (
              <>
                <FormSpacer />
                <Text size={2} color="critical1" data-test-id="wsm-series-published-error">
                  {getWsmSeriesErrorMessage(fieldError("published"), intl)}
                </Text>
              </>
            )}
            <FormSpacer />
            <Input
              type="number"
              label={intl.formatMessage(messages.sortOrder)}
              data-test-id="wsm-series-sort-order"
              disabled={disabled}
              value={String(data.sortOrder)}
              onChange={event => change({ sortOrder: Number(event.target.value) || 0 })}
              error={!!fieldError("sortOrder")}
              helperText={helper("sortOrder", intl.formatMessage(messages.sortOrderHelp))}
            />
          </DashboardCard.Content>
        </DashboardCard>
        <CardSpacer />
        <DashboardCard data-test-id="wsm-series-seo">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(messages.seoTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Input
              label={intl.formatMessage(messages.seoTitleField)}
              data-test-id="wsm-series-seo-title"
              disabled={disabled}
              value={data.seoTitle}
              onChange={event => change({ seoTitle: event.target.value })}
              error={!!fieldError("seoTitle")}
              helperText={helper("seoTitle")}
            />
            <FormSpacer />
            <Textarea
              rows={3}
              label={intl.formatMessage(messages.seoDescriptionField)}
              data-test-id="wsm-series-seo-description"
              disabled={disabled}
              value={data.seoDescription}
              onChange={event => change({ seoDescription: event.target.value })}
              error={!!fieldError("seoDescription")}
              helperText={helper("seoDescription")}
            />
          </DashboardCard.Content>
        </DashboardCard>
        <CardSpacer />
        <DashboardCard data-test-id="wsm-series-summary">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(messages.summaryTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {data.brand && (
              <>
                <Text size={2} color="default2" display="block">
                  {intl.formatMessage(messages.brand)}
                </Text>
                <Text size={3} display="block" data-test-id="wsm-series-summary-brand">
                  {data.brand}
                </Text>
                <FormSpacer />
              </>
            )}
            <Text size={3} color="default2" display="block" data-test-id="wsm-series-member-count">
              {intl.formatMessage(messages.memberCount, {
                count: typeof memberCount === "number" ? memberCount : data.products.length,
              })}
            </Text>
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.RightSidebar>

      <Savebar>
        {onDelete && <Savebar.DeleteButton onClick={onDelete} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmSeriesListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-series-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmSeriesDetailsPage;
